import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { apiFetch } from '../services/api';
import { useSocketStore } from './socket';
import { playSound } from '../utils/alarmSounds';

export type TimerStatus = 'idle' | 'running' | 'paused' | 'completed';

export interface Timer {
  id: string;
  userId: string;
  label: string;
  duration: number;
  remainingSeconds: number;
  status: TimerStatus;
  sound: string;
  startedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const useTimersStore = defineStore('timers', () => {
  const timers = ref<Timer[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  /** Client-side tick interval for smooth countdown display */
  let tickInterval: ReturnType<typeof setInterval> | null = null;

  // ── Socket listeners ────────────────────────────────────────────────────────
  const socketStore = useSocketStore();

  function handleTimerComplete(data: { timerId: string; label: string; sound: string }) {
    const t = timers.value.find((t) => t.id === data.timerId);
    if (t) {
      t.status = 'completed';
      t.remainingSeconds = 0;
      t.startedAt = null;
    }
    playSound(data.sound).catch(console.error);
  }

  function handleTimerUpdate(data: { timerId: string; status: TimerStatus; remainingSeconds: number }) {
    const t = timers.value.find((t) => t.id === data.timerId);
    if (t) {
      t.status = data.status;
      t.remainingSeconds = data.remainingSeconds;
    }
  }

  function _registerSocketListeners() {
    const s = socketStore.socket;
    if (!s) return;
    s.off('timer:complete', handleTimerComplete);
    s.off('timer:update', handleTimerUpdate);
    s.on('timer:complete', handleTimerComplete);
    s.on('timer:update', handleTimerUpdate);
  }

  function initSocketListeners() {
    _registerSocketListeners();
    watch(
      () => socketStore.socket,
      (s) => {
        if (s) _registerSocketListeners();
      },
    );
    // Client-side tick for smooth countdown
    if (!tickInterval) {
      tickInterval = setInterval(() => {
        const now = Date.now();
        timers.value.forEach((t) => {
          if (t.status === 'running' && t.startedAt) {
            const elapsed = Math.floor((now - new Date(t.startedAt).getTime()) / 1000);
            t.remainingSeconds = Math.max(0, t.duration - elapsed);
            // Note: actual completion is handled server-side via timer:complete event
          }
        });
      }, 500);
    }
  }

  // ── API ────────────────────────────────────────────────────────────────────

  async function fetchTimers() {
    loading.value = true;
    error.value = null;
    try {
      const data = await apiFetch('/timers');
      timers.value = data;
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message || 'Failed to load timers';
    } finally {
      loading.value = false;
    }
  }

  async function createTimer(payload: { label: string; duration: number; sound?: string }): Promise<Timer> {
    const timer = await apiFetch('/timers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    timers.value.unshift(timer);
    return timer;
  }

  async function startTimer(id: string): Promise<void> {
    const timer = await apiFetch(`/timers/${id}/start`, { method: 'PATCH' });
    const idx = timers.value.findIndex((t) => t.id === id);
    if (idx !== -1) timers.value[idx] = timer;
  }

  async function pauseTimer(id: string): Promise<void> {
    const timer = await apiFetch(`/timers/${id}/pause`, { method: 'PATCH' });
    const idx = timers.value.findIndex((t) => t.id === id);
    if (idx !== -1) timers.value[idx] = timer;
  }

  async function resetTimer(id: string): Promise<void> {
    const timer = await apiFetch(`/timers/${id}/reset`, { method: 'PATCH' });
    const idx = timers.value.findIndex((t) => t.id === id);
    if (idx !== -1) timers.value[idx] = timer;
  }

  async function deleteTimer(id: string): Promise<void> {
    await apiFetch(`/timers/${id}`, { method: 'DELETE' });
    timers.value = timers.value.filter((t) => t.id !== id);
  }

  return {
    timers,
    loading,
    error,
    initSocketListeners,
    fetchTimers,
    createTimer,
    startTimer,
    pauseTimer,
    resetTimer,
    deleteTimer,
  };
});
